import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaEye, FaHeart, FaComment, FaTrash } from 'react-icons/fa';
import api from '../services/api';
import '../styles/StoryDetails.css';
import getBookCoverUrl from '../utils/getBookCoverUrl';

function StoryDetails() {
  const navigate = useNavigate();
  const { storyId } = useParams();
  const fileInputRef = useRef(null);

  const [title, setTitle] = useState('İsimsiz Hikaye');
  const [description, setDescription] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [category, setCategory] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [copyright, setCopyright] = useState('Tüm Hakları Saklıdır');
  const [isMatureContent, setIsMatureContent] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [chapterToDelete, setChapterToDelete] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    handleResize(); // İlk yükleme için kontrol et
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchStoryDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get(`/story-details/${storyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { storyDetails, chapters } = response.data;
        
        chapters.sort((a, b) => a.chapter_number - b.chapter_number);

        setTitle(storyDetails.title);
        setDescription(storyDetails.description);
        setCoverImage(getBookCoverUrl(storyDetails.cover_image_url)); 
        setCategory(storyDetails.category_id);
        setCopyright(storyDetails.telif_id);
        setIsPublished(storyDetails.is_published);
        setIsMatureContent(storyDetails.is_mature_content);
        setChapters(chapters);
      } catch (error) {
        console.error('Hikaye detayları getirilirken hata oluştu:', error);
      }
    };

    fetchStoryDetails();
  }, [storyId]);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverImage(URL.createObjectURL(file));
    }
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('category_id', category);
      formData.append('telif_id', copyright);
      formData.append('is_published', isPublished);
      formData.append('is_mature_content', isMatureContent);
  
      if (fileInputRef.current.files[0]) {
        formData.append('cover', fileInputRef.current.files[0]);
      }
  
      await api.put(`/story-details/${storyId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
  
      navigate('/my-stories');
    } catch (error) {
      console.error('Hikaye kaydedilirken hata oluştu:', error);
    }
  };

  const openDeleteModal = (chapterId) => {
    setChapterToDelete(chapterId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setChapterToDelete(null);
  };

  const handleDeleteChapter = async () => {
    if (chapterToDelete !== null) {
      try {
        const token = localStorage.getItem('token');
        await api.delete(`/story-details/chapters/${chapterToDelete}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChapters(chapters.filter(chapter => chapter.chapter_id !== chapterToDelete));
        closeDeleteModal();
      } catch (error) {
        console.error('Bölüm silinirken hata oluştu:', error);
        closeDeleteModal();
      }
    }
  };

  const handleSectionClick = (chapter) => {
    navigate('/edit-story', {
      state: {
        sectionTitle: chapter.title,
        bookId: storyId,
        chapterId: chapter.chapter_id
      }
    });
  };

  const ContentTable = () => (
    <div className="content-table">
      <button className="add-section" onClick={() => handleSectionClick({ title: 'Yeni Bölüm', chapter_id: null })}>
        + Yeni Bölüm
      </button>
      {chapters.map((chapter) => (
        <div key={chapter.chapter_id} className="section">
          <div className="section-header">
            <span onClick={() => handleSectionClick(chapter)}>{chapter.title}</span>
            <span>{new Date(chapter.created_at).toLocaleDateString('tr-TR')}</span>
          </div>
          <div className="section-stats">
            <span className="stat-item"><FaEye /> {chapter.views}</span>
            <span className="stat-item"><FaHeart /> {chapter.likes}</span>
            <span className="stat-item"><FaComment /> {chapter.comments}</span>
            <FaTrash
              className="chapterdelete"
              onClick={() => openDeleteModal(chapter.chapter_id)}
            />
          </div>
        </div>
      ))}

      {showDeleteModal && (
        <div className="custom-modal">
          <p>Silmek istediğinize emin misiniz?</p>
          <button className="confirm-button" onClick={handleDeleteChapter}>Evet</button>
          <button className="cancel-button" onClick={closeDeleteModal}>Hayır</button>
        </div>
      )}
    </div>
  );

  const StoryForm = () => (
    <form>
      <div className="form-group">
        <h3>Başlık</h3>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Başlık"
          required
        />
      </div>
      <div className="form-group">
        <h3>Açıklama</h3>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Açıklama"
        />
      </div>
      <div className="form-group">
        <h3>Kategoriler</h3>
        <select
          id="category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Bir kategori seç</option>
          <option value="1">Aksiyon</option>
          <option value="2">Bilim Kurgu</option>
          <option value="3">Fantastik</option>
          <option value="4">Genel Kurgu</option>
          <option value="5">Genç Kurgu</option>
          <option value="6">Gerilim</option>
          <option value="7">Gizem</option>
          <option value="8">Hayran Kurgu</option>
          <option value="9">Klasikler</option>
          <option value="10">Korku</option>
          <option value="11">Kısa Hikaye</option>
          <option value="12">Macera</option>
          <option value="13">Mizah</option>
          <option value="14">Paranormal</option>
          <option value="15">Romantizm</option>
          <option value="16">Spiritüel</option>
          <option value="17">Tarihi Kurgu</option>
          <option value="18">Texting</option>
          <option value="19">Vampir</option>
          <option value="20">Şiir</option>
        </select>
      </div>
      <div className="form-group">
        <h3>Yetişkin İçerik</h3>
        <select
          id="isMatureContent"
          value={isMatureContent ? 'true' : 'false'}
          onChange={(e) => setIsMatureContent(e.target.value === 'true')}
        >
          <option value="false">Diğer</option>
          <option value="true">Yetişkin İçerik</option>
        </select>
      </div>
      <div className="form-group">
        <h3>Telif Hakkı</h3>
        <select
          id="copyright"
          value={copyright}
          onChange={(e) => setCopyright(e.target.value)}
        >
          <option value="Telif Hakkı Belirtilmemiş">Telif Hakkı Belirtilmemiş</option>
          <option value="Tüm Hakları Saklıdır">Tüm Hakları Saklıdır</option>
          <option value="Tüm Hakları Herkese Açık">Tüm Hakları Herkese Açık</option>
          <option value="Creative Commons (CC) İsnadı">Creative Commons (CC) İsnadı</option>
          <option value="(CC) Atıf-Gayriticari">(CC) Atıf-Gayriticari</option>
          <option value="(CC) Atıf-Gayriticari-Türetilemez">(CC) Atıf-Gayriticari-Türetilemez</option>
          <option value="(CC) Attrib. NonComm. Sürümleri ile">(CC) Attrib. NonComm. Sürümleri ile</option>
          <option value="(CC) Atıf-LisanDevam">(CC) Atıf-LisanDevam</option>
          <option value="(CC) Atıf-Türetilemez">(CC) Atıf-Türetilemez</option>
        </select>
      </div>
    </form>
  );

  return (
    <div className="story-details-page">
      <div className="new-story-container">
        <div className="story-header">
          <button onClick={handleCancel} className="back-button">{'<'}</button>
          <h1>{title || 'İsimsiz Hikaye'}</h1>
          <div className="header-buttons">
            <button onClick={handleCancel} className="cancel-button">İptal Et</button>
            <button onClick={handleSave} className="save-button">Kaydet</button>
          </div>
        </div>

        <div className="story-content">
          <div className="story-cover" onClick={openFilePicker}>
            <div className="cover-upload">
              {coverImage ? (
                <img src={coverImage} alt="Kapak" />
              ) : (
                <div className="upload-placeholder">
                  <i className="icon-placeholder" />
                  <span>Kapak ekle</span>
                </div>
              )}
              <input type="file" onChange={handleCoverImageChange} accept="image/*" ref={fileInputRef} style={{ display: 'none' }} />
            </div>
          </div>

          <div className="story-details">
            {!isMobileView ? (
              <>
                <div className="tabs">
                  <button onClick={() => setActiveTab('details')} className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}>
                    Hikaye Ayrıntıları
                  </button>
                  <button onClick={() => setActiveTab('contents')} className={`tab-button ${activeTab === 'contents' ? 'active' : ''}`}>
                    İçindekiler
                  </button>
                </div>

                {activeTab === 'details' ? <StoryForm /> : <ContentTable />}
              </>
            ) : (
              // Mobil görünüm - her iki bölümü de göster, 'İçindekiler' bölümü aşağıda
              <div className="mobile-all-content">
                <div>
                  <h1>Hikaye Ayrıntıları</h1>
                  <StoryForm />
                </div>
                
                <div className="mobile-content-section">
                  <h2 className="mobile-section-title">İçindekiler</h2>
                  <ContentTable />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StoryDetails;